import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, X } from "lucide-react";

const DEFAULT_TOAST_DURATION_MS = 2000;
const TOAST_FADE_OUT_MS = 260;
const MAX_TOASTS = 5;

const DialogContext = createContext(null);

function toastTone(type) {
  switch (type) {
    case "success":
      return {
        ring: "border-emerald-200/70 bg-slate-50/90",
        icon: "bg-emerald-100 text-emerald-700",
        bar: "bg-emerald-500",
      };
    case "error":
      return {
        ring: "border-rose-200/70 bg-slate-50/90",
        icon: "bg-rose-100 text-rose-700",
        bar: "bg-rose-500",
      };
    case "warning":
    case "warn":
      return {
        ring: "border-amber-200/70 bg-slate-50/90",
        icon: "bg-amber-100 text-amber-800",
        bar: "bg-amber-500",
      };
    case "loading":
      return {
        ring: "border-slate-200/70 bg-slate-50/90",
        icon: "bg-slate-100 text-slate-700",
        bar: "bg-slate-900",
      };
    case "info":
    default:
      return {
        ring: "border-sky-200/70 bg-slate-50/90",
        icon: "bg-sky-100 text-sky-700",
        bar: "bg-sky-500",
      };
  }
}

function ToastViewport({ toasts, dismiss, pause, resume }) {
  if (!toasts.length) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 z-[60] flex flex-col-reverse gap-2 sm:left-auto sm:bottom-4 sm:right-4 sm:w-[min(380px,calc(100vw-2rem))]">
      {toasts.map((t) => {
        const tone = toastTone(t.type);
        const durationMs = Number(t.durationMs || 0);

        const closing = Boolean(t.closing);
        const Icon = t.type === "success" ? CheckCircle2 : t.type === "error" ? AlertTriangle : t.type === "loading" ? Loader2 : Info;

        return (
          <div
            key={t.id}
            onMouseEnter={() => {
              if (t.pauseOnHover) pause(t.id);
            }}
            onMouseLeave={() => {
              if (t.pauseOnHover) resume(t.id);
            }}
            className={
              "animate-toast-in overflow-hidden rounded-2xl border shadow-sm transition-all duration-200 ease-out " +
              tone.ring +
              (closing ? " pointer-events-none translate-x-3 translate-y-2 opacity-0" : "")
            }
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3 p-4">
              <div
                className={
                  "mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl text-[0px] " +
                  tone.icon
                }
                aria-hidden="true"
              >
                <Icon className={"h-4 w-4 " + (t.type === "loading" ? "animate-spin" : "")} />
                {t.type === "success" ? "OK" : t.type === "error" ? "!" : t.type === "loading" ? "…" : "i"}
              </div>

              <div className="min-w-0 flex-1">
                {t.title ? <div className="text-sm font-semibold text-slate-900">{t.title}</div> : null}
                {t.message ? <div className="mt-0.5 text-sm text-slate-700">{t.message}</div> : null}
              </div>

              <button
                onClick={() => dismiss(t.id)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[0px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                aria-label="Tutup"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                ✕
              </button>
            </div>

            <div className="h-1 w-full bg-slate-100">
              {t.type === "loading" ? (
                <div className={"toast-indeterminate h-full w-2/5 " + tone.bar} />
              ) : durationMs > 0 ? (
                <div
                  className={"toast-progress h-full w-full " + tone.bar}
                  style={{
                    "--toast-duration": `${durationMs}ms`,
                    animationPlayState: t.paused ? "paused" : "running",
                  }}
                />
              ) : (
                <div className={"h-full w-full " + tone.bar} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DialogModal({ state, onResolve }) {
  const { open, type, title, message, content, confirmText, cancelText } = state;
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      confirmButtonRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onResolve(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onResolve]);

  if (!open) return null;

  const isConfirm = type === "confirm";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-3 sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => onResolve(false)}
        aria-hidden="true"
      />

      <div className="card relative flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden p-0 animate-bouncy">
        <div className="min-h-0 overflow-y-auto p-4 sm:p-5">
          {title ? <div className="text-base font-semibold text-slate-900">{title}</div> : null}
          {content ? (
            <div className="mt-2">{content}</div>
          ) : message ? (
            <div className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
              {message}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-white/95 p-3 sm:flex-row sm:items-center sm:justify-end sm:p-4">
          {isConfirm ? (
            <button
              onClick={() => onResolve(false)}
              className="btn btn-secondary w-full sm:w-auto"
            >
              {cancelText || "Batal"}
            </button>
          ) : null}

          <button
            ref={confirmButtonRef}
            onClick={() => onResolve(true)}
            className="btn btn-primary w-full sm:w-auto"
          >
            {confirmText || (isConfirm ? "Ya" : "OK")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DialogProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    type: "alert",
    title: "",
    message: "",
    content: null,
    confirmText: "",
    cancelText: "",
  });

  const [toasts, setToasts] = useState([]);

  const resolverRef = useRef(null);
  const toastTimersRef = useRef(new Map());
  const toastsRef = useRef([]);

  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const onResolve = useCallback(
    (result) => {
      const resolve = resolverRef.current;
      resolverRef.current = null;
      close();
      if (typeof resolve === "function") resolve(result);
    },
    [close]
  );

  const alert = useCallback((message, options = {}) => {
    const { title = "", confirmText = "OK" } = options;

    return new Promise((resolve) => {
      resolverRef.current = () => resolve();
      setState({
        open: true,
        type: "alert",
        title,
        message: String(message ?? ""),
        confirmText,
        cancelText: "",
      });
    });
  }, []);

  const confirm = useCallback((message, options = {}) => {
    const {
      title = "Konfirmasi",
      confirmText = "Ya",
      cancelText = "Batal",
    } = options;

    return new Promise((resolve) => {
      resolverRef.current = (result) => resolve(Boolean(result));
      setState({
        open: true,
        type: "confirm",
        title,
        message: String(message ?? ""),
        confirmText,
        cancelText,
      });
    });
  }, []);

  const custom = useCallback((content, options = {}) => {
  const {
    title = "",
    confirmText = "OK",
    cancelText = "",
  } = options;

  return new Promise((resolve) => {
    resolverRef.current = (result) => resolve(Boolean(result));

    setState({
      open: true,
      type: cancelText ? "confirm" : "alert",
      title,
      message: "",
      content, // 🔥 INI KUNCI
      confirmText,
      cancelText,
    });
    });
  }, []);

  const clearToastTimers = useCallback((id) => {
    const timers = toastTimersRef.current;
    const entry = timers.get(id);
    if (entry?.auto) clearTimeout(entry.auto);
    if (entry?.remove) clearTimeout(entry.remove);
    timers.delete(id);
  }, []);

  const dismissToastNow = useCallback(
    (id) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      clearToastTimers(id);
    },
    [clearToastTimers]
  );

  const requestDismissToast = useCallback(
    (id) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? (t.closing ? t : { ...t, closing: true }) : t))
      );

      const timers = toastTimersRef.current;
      const prevEntry = timers.get(id);
      if (prevEntry?.auto) clearTimeout(prevEntry.auto);
      if (prevEntry?.remove) clearTimeout(prevEntry.remove);

      const remove = setTimeout(() => dismissToastNow(id), TOAST_FADE_OUT_MS);
      timers.set(id, { auto: null, remove });
    },
    [dismissToastNow]
  );

  const scheduleAutoDismiss = useCallback(
    (id, durationMs) => {
      const nextMs = Math.max(0, Math.trunc(Number(durationMs) || 0));
      if (nextMs <= 0) return;

      const timers = toastTimersRef.current;
      const prevEntry = timers.get(id) || {};
      if (prevEntry.auto) clearTimeout(prevEntry.auto);

      const auto = setTimeout(() => requestDismissToast(id), nextMs);
      timers.set(id, {
        ...prevEntry,
        auto,
        startedAt: Date.now(),
        remainingMs: nextMs,
      });
    },
    [requestDismissToast]
  );

  const pauseToast = useCallback((id) => {
    setToasts((prev) => {
      const found = prev.find((t) => t.id === id);
      if (!found || found.type === "loading" || found.closing || found.durationMs <= 0) return prev;
      return prev.map((t) => (t.id === id ? { ...t, paused: true } : t));
    });

    const timers = toastTimersRef.current;
    const entry = timers.get(id);
    if (!entry?.auto || !entry.startedAt) return;

    clearTimeout(entry.auto);
    const elapsed = Date.now() - entry.startedAt;
    const remainingMs = Math.max(0, Number(entry.remainingMs || 0) - elapsed);

    timers.set(id, {
      ...entry,
      auto: null,
      startedAt: 0,
      remainingMs,
    });
  }, []);

  const resumeToast = useCallback(
    (id) => {
      setToasts((prev) => {
        const found = prev.find((t) => t.id === id);
        if (!found || found.type === "loading" || found.closing || found.durationMs <= 0) return prev;
        return prev.map((t) => (t.id === id ? { ...t, paused: false } : t));
      });

      const timers = toastTimersRef.current;
      const entry = timers.get(id);
      if (!entry || entry.auto || entry.remove) return;

      const remainingMs = Math.max(0, Math.trunc(Number(entry.remainingMs) || 0));
      if (remainingMs <= 0) {
        requestDismissToast(id);
        return;
      }

      scheduleAutoDismiss(id, remainingMs);
    },
    [requestDismissToast, scheduleAutoDismiss]
  );

  const toast = useCallback(
    (options = {}) => {
      const {
        type = "info",
        title = "",
        message = "",
        durationMs = DEFAULT_TOAST_DURATION_MS,
        pauseOnHover = false,
      } = options;

      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const next = {
        id,
        type,
        title: String(title || ""),
        message: String(message || ""),
        durationMs: type === "loading" ? 0 : Number(durationMs || 0),
        pauseOnHover: Boolean(pauseOnHover),
        paused: false,
        closing: false,
      };

      setToasts((prev) => [next, ...prev].slice(0, MAX_TOASTS));

      if (next.durationMs > 0) {
        scheduleAutoDismiss(id, next.durationMs);
      }

      return id;
    },
    [scheduleAutoDismiss]
  );

  const updateToast = useCallback(
    (id, patch = {}) => {
      const current = toastsRef.current.find((t) => t.id === id);
      const nextType = patch.type || current?.type || "info";
      const nextDurationMs =
        nextType === "loading"
          ? 0
          : Number(patch.durationMs ?? current?.durationMs ?? DEFAULT_TOAST_DURATION_MS);

      setToasts((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;

          const merged = {
            ...t,
            ...patch,
            title: patch.title != null ? String(patch.title) : t.title,
            message: patch.message != null ? String(patch.message) : t.message,
            type: patch.type || t.type,
            pauseOnHover: patch.pauseOnHover != null ? Boolean(patch.pauseOnHover) : t.pauseOnHover,
            paused: false,
          };

          return { ...merged, durationMs: nextDurationMs };
        })
      );

      clearToastTimers(id);

      if (nextType !== "loading" && nextDurationMs > 0) {
        scheduleAutoDismiss(id, nextDurationMs);
      }
    },
    [clearToastTimers, scheduleAutoDismiss]
  );

  const toastPromise = useCallback(
    async (promise, messages = {}, options = {}) => {
      const {
        loading = "Memproses…",
        success = "Berhasil",
        error = "Gagal",
      } = messages;

      const id = toast({
        type: "loading",
        title: options.loadingTitle || "Loading",
        message: typeof loading === "function" ? loading() : loading,
      });

      try {
        const res = await promise;
        updateToast(id, {
          type: "success",
          title: options.successTitle || "Selesai",
          message: typeof success === "function" ? success(res) : success,
          durationMs: options.successDurationMs ?? DEFAULT_TOAST_DURATION_MS,
        });
        return res;
      } catch (e) {
        updateToast(id, {
          type: "error",
          title: options.errorTitle || "Gagal",
          message: typeof error === "function" ? error(e) : error,
          durationMs: options.errorDurationMs ?? DEFAULT_TOAST_DURATION_MS,
        });
        throw e;
      }
    },
    [toast, updateToast]
  );

  const value = useMemo(
    () => ({ alert, confirm, custom, toast, dismissToast: requestDismissToast, updateToast, toastPromise }),
    [alert, confirm, custom, toast, requestDismissToast, updateToast, toastPromise]
  );

  useEffect(() => {
    const timers = toastTimersRef.current;
    return () => {
      timers.forEach((entry) => {
        if (entry?.auto) clearTimeout(entry.auto);
        if (entry?.remove) clearTimeout(entry.remove);
      });
      timers.clear();
    };
  }, []);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <DialogModal state={state} onResolve={onResolve} />
      <ToastViewport toasts={toasts} dismiss={requestDismissToast} pause={pauseToast} resume={resumeToast} />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used within <DialogProvider>");
  }
  return ctx;
}
